import type { SearchParameters } from './searchParameters';
import {
  AssetFamilySearchFilterEnum,
  AssetTypeSearchFilterEnum,
  EditorialProductEnum,
  OfflineContentSearchFilterEnum,
} from './filterValues';

// Small, intention-revealing predicates over a search's asset type and family.
// They are shared by the business rules (parameter availability, applied-filter
// exclusion) so the definitions live in one place and each rule reads as a
// single line.

export type SearchPredicate = (searchParameters: SearchParameters) => boolean;

// --- Asset-type traits ---
export const isImage: SearchPredicate = (searchParameters) =>
  searchParameters.assettype === AssetTypeSearchFilterEnum.IMAGE;

export const isFilm: SearchPredicate = (searchParameters) =>
  searchParameters.assettype === AssetTypeSearchFilterEnum.FILM;

export const isImageAndFilm: SearchPredicate = (searchParameters) =>
  searchParameters.assettype === AssetTypeSearchFilterEnum.IMAGE_AND_FILM;

// An events filter narrows the search to those events' own results, so it is
// no longer grouped by event even if groupbyevent is still set.
export const isGroupedByEvent: SearchPredicate = (searchParameters) =>
  searchParameters.groupByEvent && !searchParameters.events;

export const isOfflineContentOnly: SearchPredicate = (searchParameters) =>
  searchParameters.offlinecontent === OfflineContentSearchFilterEnum.EXCLUSIVE;

export const isVideoTemplate: SearchPredicate = (searchParameters) =>
  searchParameters.assettype === AssetTypeSearchFilterEnum.VIDEO_TEMPLATE;

// "Includes image" covers a still-image search and a combined image-and-film
// search — the asset types that surface still-image filters.
export const assetTypeIncludesImage: SearchPredicate = (searchParameters) =>
  isImage(searchParameters) || isImageAndFilm(searchParameters);

// Any photographic asset type: a still image, film, or the two combined
// (i.e. anything other than a video-template search).
export const assetTypeIsImageFilmOrBoth: SearchPredicate = (searchParameters) =>
  isImage(searchParameters) ||
  isFilm(searchParameters) ||
  isImageAndFilm(searchParameters);

// --- Phrase traits ---
export const isBlankPhrase: SearchPredicate = (searchParameters) =>
  !searchParameters.phrase;

// --- Family traits ---
export const isCreativeOnly: SearchPredicate = (searchParameters) =>
  searchParameters.family === AssetFamilySearchFilterEnum.CREATIVE;

export const isEditorialOnly: SearchPredicate = (searchParameters) =>
  searchParameters.family === AssetFamilySearchFilterEnum.EDITORIAL;

// "Includes creative" / "includes editorial" are true for the matching
// single-family search and for the combined creative-and-editorial search.
export const familyIncludesCreative: SearchPredicate = (searchParameters) =>
  searchParameters.family === AssetFamilySearchFilterEnum.CREATIVE ||
  searchParameters.family ===
    AssetFamilySearchFilterEnum.CREATIVE_AND_EDITORIAL;

export const familyIncludesEditorial: SearchPredicate = (searchParameters) =>
  searchParameters.family === AssetFamilySearchFilterEnum.EDITORIAL ||
  searchParameters.family ===
    AssetFamilySearchFilterEnum.CREATIVE_AND_EDITORIAL;

// The search covers both families at once. Equivalent to asking both
// `familyIncludesCreative` and `familyIncludesEditorial`, which collapses to
// this single comparison because the family enum has only the three members.
export const isCreativeAndEditorial: SearchPredicate = (searchParameters) =>
  searchParameters.family ===
  AssetFamilySearchFilterEnum.CREATIVE_AND_EDITORIAL;

// --- Agreement traits ---

// True when the search is scoped by any agreement: premium access, easy
// access, editorial subscription, or royalty-free subscription.
export const isFilteredByAgreement: SearchPredicate = (searchParameters) =>
  searchParameters.hasAgreements;

// --- Editorial-product traits ---

// An archival search on the editorial-only family.
//
// Scoped to the editorial-only family. Mixed-family archival is not a
// combination the search UI supports, so it is left out rather than modelled;
// see the editorial-archival cases in predicates.test.ts.
export const isEditorialArchival: SearchPredicate = (searchParameters) =>
  isEditorialOnly(searchParameters) &&
  searchParameters.editorialproducts === EditorialProductEnum.ARCHIVAL;
